import { useTranslation } from 'react-i18next'
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline'
import { notAvailableFeature } from './cycleStatus'

const ICONS = {
  completed: { Icon: CheckCircleIcon, className: 'text-green-600' },
  failed: { Icon: XCircleIcon, className: 'text-red-600' },
  running: { Icon: ArrowPathIcon, className: 'text-primary-600 animate-spin' },
  pending: { Icon: ClockIcon, className: 'text-gray-400' },
  skipped: { Icon: MinusCircleIcon, className: 'text-gray-400' },
}

// VH-227: one row per runner step with its status; failed steps can be retried.
export function CycleStepProgress({ steps = [], onRetry, retrying = false, readOnly = false }) {
  const { t } = useTranslation()
  const done = steps.filter((s) => !['pending', 'running'].includes(s.status)).length

  return (
    <section className="card" aria-label={t('healthCycle.steps.title')}>
      <div className="card-body">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">{t('healthCycle.steps.title')}</h3>
          <span className="text-xs text-gray-500">
            {t('healthCycle.steps.progress', { done, total: steps.length })}
          </span>
        </div>
        <ul className="divide-y divide-gray-100">
          {steps.map((step) => {
            const { Icon, className } = ICONS[step.status] || ICONS.pending
            const feature = notAvailableFeature(step)
            return (
              <li key={step.step} className="py-2 flex items-start justify-between gap-3" data-testid={`step-${step.step}`}>
                <div className="flex items-start gap-2 min-w-0">
                  <Icon className={`h-5 w-5 flex-shrink-0 ${className}`} aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900">{t(`healthCycle.steps.names.${step.step}`, step.step)}</p>
                    <p className="text-xs text-gray-500">
                      {feature
                        ? t('healthCycle.steps.notAvailableYet', { feature })
                        : step.status === 'skipped' && step.reason?.startsWith('dependency_failed')
                          ? t('healthCycle.steps.dependencyFailed', {
                              step: t(`healthCycle.steps.names.${step.reason.split(':')[1]}`, step.reason.split(':')[1]),
                            })
                          : step.status === 'skipped' && step.reason
                            ? t(`healthCycle.steps.reasons.${step.reason}`, t('healthCycle.steps.status.skipped'))
                            : t(`healthCycle.steps.status.${step.status}`, step.status)}
                    </p>
                  </div>
                </div>
                {step.status === 'failed' && !readOnly && onRetry && (
                  <button
                    type="button"
                    className="btn-outline text-xs"
                    disabled={retrying}
                    onClick={() => onRetry(step.step)}
                  >
                    {t('healthCycle.steps.retry')}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}

export default CycleStepProgress
